// Services
import breedersService from "@/app/services/main/breeders/breeders-service.js";
import addressFinderAutocompleteService from "@/app/services/address-finder/address-finder-autocomplete/address-finder-autocomplete-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins";

// Constants
import { STATUS_CODES } from "@/app/constants/status-constants";
import { STATES } from "@/app/constants/state-constants.js";

// Component
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );

export default {
    name: "AuthBreederRegister",
    components: { FileUpload },
    mixins: [
        toastMessageMixins,
        imageCompressorMixins,
        inputTextMixins,
        userMixins,
    ],
    data() {
        return {
            // Breeder Details
            breederDetails: {
                breederId: "",
                state: "",
                firstName: "",
                lastName: "",
                emailAddress: "",
                mobileNumber: "",
                address: "",
                suburb: "",
                postcode: "",
                status: "DEACTIVATED",
                companyName: "",
            },

            // Image upload
            imageToCrop: "",
            imageSrc: "",
            fileBlob: null,

            // States
            stateOptions: [
                {
                    value: "NSW",
                    option: "New South Wales",
                },
                {
                    value: "VIC",
                    option: "Victoria",
                },
                {
                    value: "QLD",
                    option: "Queensland",
                },
                {
                    value: "WA",
                    option: "Western Australia",
                },
                {
                    value: "SA",
                    option: "South Australia",
                },
                {
                    value: "TAS",
                    option: "Tasmania",
                },
                {
                    value: "NT",
                    option: "Northern Territory",
                },
                {
                    value: "ACT",
                    option: "Australian Capital Territory",
                },
            ],

            // Address List
            addressList: [],
            isAddressLoading: false,

            // Loading
            isAPILoading: false,

            // Mobile Number Format
            mobileNumberIsValid: true,
            breederMobileNumberIsValid: true,
            imageTypeList: ['image/svg','image/png','image/jpg','image/jpeg','image/gif','image/raw'],
        };
    },

    created() {},
    computed: {
        hasImage() {
            return !!this.imageSrc;
        },

        selectedState() {
            let state = "";

            if (this.breederDetails.state) {
                state = STATES[this.breederDetails.state];
            } else {
                // state = "Select State";
            }

            return state;
        },

        validationRules() {
            let fields = {
                breederDetails: {
                    breederId: "",
                    state: {},
                    firstName: "",
                    lastName: "",
                    emailAddress: {},
                    mobileNumber: {},
                    address: {},
                    companyName: {},
                },
            };

            fields.breederDetails = vuelidateUtil.setFieldsRequired(
                fields.breederDetails,
                true
            );
            fields.breederDetails = vuelidateUtil.setEmailValidation(
                fields.breederDetails,
                "emailAddress"
            );

            return fields;
        },

        isDisabled() {
            return this.$v.$invalid || this.isAPILoading;
        },
    },

    methods: {
        async submitBreederDetails() {
            if (this.$v.$invalid || !this.mobileNumberIsValid) {
                return;
            }

            try {
                this.isAPILoading = true;

                const {
                    breederId,
                    state,
                    firstName,
                    lastName,
                    emailAddress,
                    mobileNumber,
                    address,
                    suburb,
                    postcode,
                    companyName,
                } = this.breederDetails;

                const formattedAddress = address.fullAddress
                const formattedEmail = emailAddress.toLowerCase();

                // role 7 is for breeder roles
                let breederDetails = {
                    formDetails: {
                        userDetails: {
                            firstName: firstName,
                            lastName: lastName,
                            email: formattedEmail,
                            defaultTimezone: "Australia/Sydney",
                        },
                        breederDetails: {
                            firstName,
                            lastName,
                            defaultTimezone: "Australia/Sydney",
                            email: formattedEmail,
                            address: formattedAddress,
                            suburb,
                            postcode,
                            mobileNumber: mobileNumber.replace("-", ""),
                            code: breederId,
                            state,
                            companyName,
                            role: 7,
                        },
                    },
                    profileImage: this.fileBlob
                        ? this.fileBlob[0]
                        : this.isBreederAvailable
                        ? this.imageSrc
                        : "",
                };

                // Call API for register breeder and user
                const response = await breedersService.registerUserAndBreeder(
                    breederDetails
                );

                if (
                    response.status === STATUS_CODES.SUCCESS ||
                    response.status === STATUS_CODES.SUCCESS_NO_CONTENT
                ) {
                    this.$swal({
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/success-dog.png")}`,
                        imageAlt: "Success Dog",
                        title: "Awesome!",
                        text: "Please check your email for verification.",
                        confirmButtonColor: "#4B8BFF",
                        confirmButtonText: "Got it",
                    }).then(() => {
                        this.$router.push("/");
                    });

                    this.isAPILoading = false;
                } else {
                    // Call Error Toast
                    this.showAPIErrorToast(
                        "Unable to create an account.",
                        response
                    );
                    this.isAPILoading = false;
                }
            } catch (err) {
                this.showGenericErrorToast();
                this.isAPILoading = false;
            }
        },

        onSelectedState(state) {
            this.breederDetails.state = state.value;
        },

        async onBlur(field) {
            if (field === "address") {
                this.$v.breederDetails.address.$touch();

                if (this.breederDetails.address) {
                    await this.getAddressMetaData(this.breederDetails.address);
                }
            }
        },
        async setSelectedAddress() {
            this.$v.breederDetails.address.$touch();

            if (this.breederDetails.address) {
                await this.getAddressMetaData(this.breederDetails.address);
            }
        },

        findAddress(address) {
            if (address) {
                this.isAddressLoading = true;

                this.delayTyping(() => {
                    this.getAutocompleteAddress(address);
                });
            }
        },

        getAutocompleteAddress(address) {
            addressFinderAutocompleteService
                .getAutocompleteAddress(address)
                .then((res) => {
                    if (res.status === STATUS_CODES.SUCCESS) {
                        this.addressList = res.data.completions.map((item) => {
                            let tempAddress = item.full_address;
                            item.displayAddress = tempAddress;
                            item.label = item.id;
                            return item;
                        });
                        this.isAddressLoading = false;
                    } else {
                        const { ERROR_GET_ADDRESS } =
                            this.toastMessages.addressFinder();
                        this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
                        this.isAddressLoading = false;
                    }
                })
                .catch(() => {
                    this.showGenericErrorToast();
                    this.isAddressLoading = false;
                });
        },

        async getAddressMetaData(address) {
            addressFinderAutocompleteService
                .getAddressMetaData(address.id)
                .then((res) => {
                    if (res.status === STATUS_CODES.SUCCESS) {
                        const { locality_name, postcode, full_address } = res.data;
                        this.breederDetails.address.displayAddress =
                            locality_name + " " + postcode;
                        this.breederDetails.address.fullAddress = full_address
                        this.breederDetails.postcode = postcode
                        this.breederDetails.suburb = locality_name
                    } else {
                        const { ERROR_GET_ADDRESS } =
                            this.toastMessages.addressFinder();
                        this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
                        this.isAddressLoading = false;
                    }
                })
                .catch(() => {
                    this.showGenericErrorToast();
                    this.isAddressLoading = false;
                });
        },

        async onUploadImage(event) {
            let valid = await this.validateImageWithType(event.target.files[0], this.imageTypeList);
            
            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.uploader.value = null
                this.imageSrc = null;
                return;
            } else {
                // compress image if more than 2mb
                const file = await this.compressImage(event.target.files[0]);

                this.fileBlob = [file];

                // show preview of pet image
                let reader = new FileReader();
                reader.onload = () => {
                    this.imageToCrop = reader.result;
                    this.$refs.modalFileUpload.show();
                };
    
                reader.readAsDataURL(file);
            }
        },

        setImageCrop({ image, blob }) {
            this.imageSrc = image;
            this.fileBlob = blob;
        },

        clearFileInput(event) {
            event.target.value = "";
        },

        mobileNumberFormat(event) {
            this.mobileNumberIsValid = true;

            this.breederDetails.mobileNumber = this.formatMobileNumberOnChange(event);
        },
        onPasteMobileNumber(event) {
			event.preventDefault(); // Prevent default paste behavior
			const pastedText = event.clipboardData.getData('text/plain');

			const formatMobileNumberOnPaste = this.formatMobileNumberOnPaste(pastedText)
			event.target.value = formatMobileNumberOnPaste;
		},
        checkFormat() {
            let mobileNumber = this.breederDetails.mobileNumber;
            let validMobile = this.breederMobileNumberIsValid;

            this.$v.breederDetails.mobileNumber.$touch();

            let regexp = /^\d{4}-\d{6}$/;

            if (mobileNumber !== null && mobileNumber !== "") {
                validMobile = regexp.test(mobileNumber);
            } else {
                validMobile = true;
            }

            this.mobileNumberIsValid = validMobile;
        },
        contactUsEmail() {
            window.location.href = "mailto:services@lovemydogclub.com.au";
        },
    },
    validations() {
        return this.validationRules;
    },
};
